// The stacked tab chrome shared by every multi-level hub, so tab strips cannot drift
// apart. Presentation only: the caller decides which item is active and where each
// links, since URL-to-tab resolution is hub-specific.
import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import styles from "./TabRows.module.css";

export interface TabRowItem {
  // Relative paths resolve against the layout's own route, wherever it is mounted.
  to: string;
  label: string;
  active: boolean;
  badge?: ReactNode;
  // A second line under the label; only the first row renders it.
  meta?: ReactNode;
  // An internal view, set apart from the operator's tabs after a divider.
  debug?: boolean;
  // Starts a new group of tabs, after a divider.
  dividerBefore?: boolean;
  // Starts a named group: the caption before it, after a divider unless it opens the row.
  group?: string;
}

export interface TabRow {
  ariaLabel: string;
  items: TabRowItem[];
  // Shown at the row's right-hand end.
  aside?: ReactNode;
}

// Rows render top to bottom in one white card. The first is underlined tabs, the
// rest are pills, so two adjacent rows never read as one flat strip.
export const TabRows = ({ rows }: { rows: TabRow[] }) => {
  // A row with one item offers no choice.
  if (!rows.some((row) => row.items.length >= 2)) return null;
  return (
    <div className={styles.card}>
      {rows.map((row, level) => {
        if (row.items.length < 2) return null;
        const isTop = level === 0;
        return (
          <nav
            key={row.ariaLabel}
            className={isTop ? styles.tabs : styles.subTabs}
            aria-label={row.ariaLabel}
          >
            {row.items.map((item, index) => (
              <Fragment key={item.to}>
                {index > 0 &&
                  (item.dividerBefore ||
                    item.group ||
                    (item.debug && !row.items[index - 1]?.debug)) && (
                  <span className={styles.divider} aria-hidden="true" />
                )}
                {item.group && <span className={styles.groupLabel}>{item.group}</span>}
                <Link
                  to={item.to}
                  className={[
                    isTop ? styles.tab : styles.subTab,
                    item.active ? (isTop ? styles.tabActive : styles.subTabActive) : "",
                    item.debug ? styles.debug : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-current={item.active ? "page" : undefined}
                >
                  <span>
                    {item.debug ? (
                      <span className={styles.debugLabel}>{item.label}</span>
                    ) : (
                      item.label
                    )}
                  </span>
                  {item.badge}
                  {isTop && item.meta && (
                    <span className={styles.meta}>{item.meta}</span>
                  )}
                </Link>
              </Fragment>
            ))}
            {row.aside && <span className={styles.aside}>{row.aside}</span>}
          </nav>
        );
      })}
    </div>
  );
};
